'use client'

import React, { useEffect, useRef, useState } from 'react'

interface BeamPath {
  x1: number
  y1: number
  x2: number
  y2: number
}

export function PrismToCardBeam({
  sourceSelector = '#prism-root svg',
  targetSelector = '#main-plan-card',
}: {
  sourceSelector?: string
  targetSelector?: string
}) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [path, setPath] = useState<BeamPath | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const update = () => {
      const overlay = overlayRef.current
      const source = document.querySelector(sourceSelector) as HTMLElement | null
      const target = document.querySelector(targetSelector) as HTMLElement | null
      if (!overlay || !source || !target) return

      const containerRect = overlay.getBoundingClientRect()
      const srcRect = source.getBoundingClientRect()
      const tgtRect = target.getBoundingClientRect()

      const x1 = srcRect.left + srcRect.width / 2 - containerRect.left
      const y1 = srcRect.top + srcRect.height / 2 - containerRect.top
      const x2 = tgtRect.left - containerRect.left
      const y2 = y1 // force horizontal beam

      setPath({ x1, y1, x2, y2 })
    }

    update()
    const timeout = setTimeout(update, 100)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, { passive: true })
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update)
    }
  }, [sourceSelector, targetSelector])

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect() // Only trigger once
        }
      },
      { threshold: 0.3 } // Trigger when 30% visible
    )

    if (overlayRef.current) {
      observer.observe(overlayRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const gradId = 'beamGradient'

  return (
    <div 
      ref={overlayRef} 
      className="pointer-events-none absolute inset-0" 
      style={{ 
        zIndex: 0,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.8s ease-out 4.4s'
      }}
    >
      <svg className="absolute inset-0" width="100%" height="100%">
        {path ? (
          <>
            <defs>
              <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1={path.x1} y1={path.y1} x2={path.x2} y2={path.y2}>
                <stop offset="0%" stopColor="#FAF9F5" />
                <stop offset="100%" stopColor="#C5ECAC" />
              </linearGradient>
            </defs>
            <line
              x1={path.x1}
              y1={path.y1}
              x2={path.x2}
              y2={path.y2}
              stroke={`url(#${gradId})`}
              strokeWidth={20}
              strokeLinecap="round"
            />
          </>
        ) : null}
      </svg>
    </div>
  )
}
